import * as React from 'react'
import { createTranslator, type Locale, type Translator } from '@/lib/i18n'

export type Appearance = 'light' | 'dark'
export type Density = 'comfortable' | 'compact'

type VisitOneContextValue = {
  appearance: Appearance
  setAppearance: (value: Appearance) => void
  toggleAppearance: () => void
  density: Density
  setDensity: (value: Density) => void
  locale: Locale
  setLocale: (value: Locale) => void
  t: Translator
}

const VisitOneContext = React.createContext<VisitOneContextValue | null>(null)

export function VisitOneProvider({
  children,
  defaultAppearance = 'light',
  defaultDensity = 'comfortable',
  defaultLocale = 'en-GB',
}: {
  children: React.ReactNode
  defaultAppearance?: Appearance
  defaultDensity?: Density
  defaultLocale?: Locale
}) {
  const [appearance, setAppearance] = React.useState<Appearance>(defaultAppearance)
  const [density, setDensity] = React.useState<Density>(defaultDensity)
  const [locale, setLocale] = React.useState<Locale>(defaultLocale)

  // The theme attribute swaps every semantic token value in one place.
  React.useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-visitone-theme', appearance)
    root.setAttribute('data-visitone-density', density)
    root.setAttribute('lang', locale)
  }, [appearance, density, locale])

  const t = React.useMemo(() => createTranslator(locale), [locale])

  const value = React.useMemo<VisitOneContextValue>(
    () => ({
      appearance,
      setAppearance,
      toggleAppearance: () => setAppearance((prev) => (prev === 'light' ? 'dark' : 'light')),
      density,
      setDensity,
      locale,
      setLocale,
      t,
    }),
    [appearance, density, locale, t],
  )

  return <VisitOneContext.Provider value={value}>{children}</VisitOneContext.Provider>
}

export function useVisitOne() {
  const context = React.useContext(VisitOneContext)
  if (!context) throw new Error('useVisitOne must be used within a VisitOneProvider')
  return context
}

export function useT() {
  return useVisitOne().t
}
